import throwErrorIfTesting from "@insite/client-framework/Common/ThrowErrorIfTesting";
import { ApiHandler, createHandlerChainRunner, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import { AddressCollectionModel, getAutocompleteShipTos, getInternationalAutocompleteShipTos, getInternationalAutocompleteSummaryShipTos } from "@store/Services/CheckoutService";
import { StoreHandlerProps} from "@store/Store/HandlerCreator";
import isCountryCodeUS from "@store/utilities/isCountryCodeUS";

export interface AutoCompleteAddressModel extends ApiParameter {
    searchTerm?: string | undefined;
    country: string;
    addressId?: string | undefined;
    entries?: number | undefined;
    forceToUSAutocomplete?: boolean | undefined;
}

type Parameter = (AutoCompleteAddressModel) &
    HasOnSuccess<AddressCollectionModel> &
    HasOnError<string>;
type Props = StoreHandlerProps & {
    apiParameter: AutoCompleteAddressModel;
    apiResult: AddressCollectionModel;
};
type HandlerType = ApiHandler<Parameter, AddressCollectionModel, Props>;

export const DispatchBeginAutocomplete: HandlerType = props => {
    throwErrorIfTesting();

    props.dispatch({
        type: "Store/Pages/CheckoutShipping/BeginAutocomplete",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = props.parameter;
};

export const SendDataToApi: HandlerType = async props => {
    const useUSAutocomplete = isCountryCodeUS(props.parameter.country) || props.parameter.forceToUSAutocomplete;
    try {
        let result;
        if(useUSAutocomplete){
            const apiParameterWithCountry = {
                ...props.apiParameter,
                countryCode: props.parameter.country,
            };
            result = await getAutocompleteShipTos(apiParameterWithCountry);
        }
        else if(!useUSAutocomplete && (Number(props.parameter.entries) <= 1)){
            result = await getInternationalAutocompleteShipTos(props.apiParameter);
        }
        else{
            result = await getInternationalAutocompleteSummaryShipTos(props.apiParameter);
        }

        if (result.successful) {
            props.apiResult = result.result;
        } else {
            props.parameter.onError?.(result.errorMessage!);
        }
    } catch (error) {
        props.parameter.onError?.(error);
    }
};

export const DispatchCompleteReorder: HandlerType = props => {
    props.dispatch({
        type: "Store/Pages/CheckoutShipping/CompleteAutocomplete",
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if(props.apiResult){
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const chain = [
    DispatchBeginAutocomplete,
    PopulateApiParameter,
    SendDataToApi,
    DispatchCompleteReorder,
    ExecuteOnSuccessCallback,
];

const addressAutocomplete = createHandlerChainRunner(chain, "AddressAutocomplete");
export default addressAutocomplete;